import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  SelectOption,
  useNotify,
  UserPermission,
  ColumnFilter
} from '@globaleris/ui.library'
import { Pageview, Delete, InsertDriveFile } from '@material-ui/icons'

import { DataTableErrorHandler } from '../../../../components/errors/scopes/dataTable/dataTableError'
import DateFilter from '../../../../components/shared/dateFilter/dateFilter'
import CustomFilter from '../../../../components/shared/customFilter/customFilter'
import { ErrorDetailList } from '../../../../components/errorDetailList/errorDetailList'
import { getUserPermissions } from '../../../../helpers/permissionsHelper'
import { NonAuthRoutes } from '../../../../model'
import { DateFilters, MAX_DATATABLE_RESULTS } from '../../../../model'
import { getCustomerDomain } from '../../../../redux'
import { enumToSelectOptionArray } from '../../../../helpers'
import { EnumTipoPosicion, ValidationType } from '../../../../api'
import { TradeListItemViewModel, FetchTradesRequest } from '../../trades.model'
import { TRADES_TABS } from '../../trades'
import { Pages } from '../../../../model/RBAC/constants'
import {
  countTrades,
  fetchCounterParts,
  fetchDeliveryPoints,
  fetchCounterPartsExtendedClients,
  fetchTrades,
  deleteTradesItems
} from '../../trades.service'
import {
  storeCustomFilterForCurrentPage,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage,
  storeDateFilterForCurrentPage
} from '../../../../redux'
import { useSelector } from 'react-redux'
import { RootStoreModel } from '../../../../redux'
import { useHttpError } from '../../../../hooks/useHttpError'
import { sortTradesByDateAndTime, extractDistinctLogisticElements } from '../../trades.mapper'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'
import { useConfirmationDialog, useErrorDetailListDialog } from '../../../../hooks'
import { EnumDocumentsNavigationFrom } from '../../../documents/documents.model'
import { EnumCommodity } from '../../../../api/Argentia/enums'

interface TradesTableProps {
  isFetching?: boolean
  currentTab: TRADES_TABS | null
  errorProps: any
  dataTableFilter: any
  dataTableSearch: string
  customFilter: any
  dateFilter: any
  subjectID?: number
  goBackTo?: string
  hideAddButton?: boolean
}

export const TradesTable: React.FC<TradesTableProps> = ({
  currentTab,
  errorProps,
  dataTableFilter,
  dataTableSearch,
  customFilter,
  dateFilter,
  subjectID,
  goBackTo,
  hideAddButton = false
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'trades'])
  const history = useHistory()
  const { showError } = useHttpError()
  const { info } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [trades, setTrades] = useState<TradeListItemViewModel[] | undefined>(undefined)
  const [logisticElements, setLogisticElements] = useState<SelectOption[] | undefined>(undefined)
  const [searchFormDependencies, setSearchFormDependencies] = useState({
    deliveryPoints: [] as SelectOption[],
    counterparts: [] as SelectOption[],
    extendedClients: [] as SelectOption[]
  })

  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone! && state.profile?.activeBalanceZone
  )

  const getInitialFormDependencies = async () => {
    setSearchFormDependencies({
      counterparts: await fetchCounterParts(),
      extendedClients: await fetchCounterPartsExtendedClients(),
      deliveryPoints: await fetchDeliveryPoints(activeBalanceZone?.id)
    })
  }

  useEffect(() => {
    getInitialFormDependencies()
  }, [activeBalanceZone])

  const onViewDetailClickHandler = (row: TradeListItemViewModel) => {
    history.push(NonAuthRoutes.tradesEdit, {
      trade: row,
      currentTab: currentTab,
      navigation: goBackTo
    })
  }

  const searchFilterInputs = [
    {
      name: 'deliveryPoint',
      label: t('translation:components.searchFilter.inputs.deliveryPoint'),
      type: DataType.SELECT,
      selectOptions: searchFormDependencies.deliveryPoints
    },
    ...(currentTab === TRADES_TABS.GENERAL || (currentTab === TRADES_TABS.ALL && !subjectID)
      ? [
          {
            name: 'counterpart',
            label: t('translation:components.searchFilter.inputs.counterpart'),
            type: DataType.SELECT,
            selectOptions: searchFormDependencies.counterparts
          }
        ]
      : currentTab === TRADES_TABS.EXTENDED_CLIENT && !subjectID
        ? [
            {
              name: 'counterpart',
              label: t('translation:components.searchFilter.inputs.extendedClient'),
              type: DataType.SELECT,
              selectOptions: searchFormDependencies.extendedClients
            }
          ]
        : [])
  ]

  const fetch = async (request: FetchTradesRequest) => {
    setIsFetching(true)
    setTrades(undefined)

    await fetchTrades({
      ...request,
      tab: currentTab
    }).then(response => {
      if (response.success) {
        const sortedTrades = sortTradesByDateAndTime(response.data)
        setTrades(sortedTrades)
        setLogisticElements(extractDistinctLogisticElements(sortedTrades))
      } else {
        showError({ errorCode: response?.error?.code })
      }
      setIsFetching(false)
    })
  }

  const reloadPage = async () => {
    await fetch({
      initDate: customFilter?.startDate || moment().toDate(),
      endDate: customFilter ? customFilter.endDate : moment().toDate(),
      dateFilter: customFilter
        ? DateFilters.CUSTOM
        : dateFilter
          ? dateFilter.activeDateFilter!
          : DateFilters.ACTIVES,
      counterpart: subjectID ?? (customFilter ? customFilter.counterpart : undefined),
      deliveryPoint: customFilter ? customFilter.deliveryPoint : undefined,
      tab: currentTab
    })
  }

  useEffect(() => {
    if ((dateFilter || activeBalanceZone) && currentTab != null) {
      reloadPage()
    }
  }, [dateFilter, activeBalanceZone, currentTab])

  const onApplyCustomFilter = async (filterToApply: any) => {
    await fetch({
      initDate: filterToApply.startDate,
      endDate: filterToApply.endDate,
      dateFilter: undefined,
      counterpart: subjectID ?? filterToApply.counterpart,
      deliveryPoint: filterToApply.deliveryPoint,
      tab: currentTab
    })
    storeCustomFilterForCurrentPage(filterToApply)
  }

  const onPrevalidateCustomFilter = async (filterToApply: any): Promise<boolean> => {
    const resultCount = await countTrades({
      initDate: filterToApply.startDate,
      endDate: filterToApply.endDate,
      dateFilter: undefined,
      counterpart: subjectID ?? filterToApply.counterpart,
      deliveryPoint: filterToApply.deliveryPoint,
      tab: currentTab
      // clientId
    })
    return resultCount !== undefined && resultCount < MAX_DATATABLE_RESULTS
  }

  const onDeleteClickHandler = async (row: TradeListItemViewModel) => {
    getConfirmation(ConfirmationDialogs.DELETE).then(async () => {
      deleteTradesItems(row.id).then(async response => {
        if (response.success) {
          info(
            t('trades:notifications.delete.success', {
              tradeCode: row?.codTrade
            })
          )
          reloadPage()
        } else {
          showErrorDetailListDialog(
            response.error?.message ?? 'Se ha producido un error',
            response.error ? [response.error] : [{ code: '412', message: '8001' }],
            t('trades:notifications.delete.error')
          )
        }
      })
      closeConfirmation()
    })
  }

  const onDocumentAddHandler = async (row: TradeListItemViewModel) => {
    history.push(NonAuthRoutes.Documents, {
      elementId: row.id,
      elementCode: row.codTrade,
      commodity: row.commodity ?? EnumCommodity.GasNatural,
      navigation: EnumDocumentsNavigationFrom.Trades
    })
  }

  const onDateFilterChange = (
    startDate: Date,
    endDate: Date,
    activeDateFilter?: DateFilters
  ): void => {
    storeDateFilterForCurrentPage({
      activeDateFilter: activeDateFilter,
      initDate: startDate,
      endDate: endDate
    })
  }

  const columns: DataTableColumn[] = [
    {
      name: 'signDate',
      label: t('trades:dataTable.columns.signDate'),
      type: DataType.DATE
    },
    {
      name: 'codTrade',
      label: t('trades:dataTable.columns.codTrade'),
      type: DataType.TEXT,
      columnWidth: 200
    },
    {
      name: 'counterpart',
      label: t('trades:dataTable.columns.counterpart'),
      type: DataType.TEXT
    },
    {
      name: 'description',
      label: t('trades:dataTable.columns.description'),
      type: DataType.TEXT,
      columnWidth: 200
    },
    {
      name: 'tradeType',
      label: t('trades:dataTable.columns.tradeType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumTipoPosicion, 'enums:EnumTipoPosicion')
    },
    {
      name: 'deliveryPointsIds',
      label: t('trades:dataTable.columns.deliveryPoint'),
      type: DataType.SELECT,
      selectOptions: logisticElements
    },
    {
      name: 'initDate',
      label: t('trades:dataTable.columns.initDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('trades:dataTable.columns.endDate'),
      type: DataType.DATE
    },
    {
      name: 'validation',
      label: t('trades:dataTable.columns.validation'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(ValidationType, 'enums:ValidationType').map(option => {
        return { ...option, value: ValidationType[option.value] }
      })
    }
  ]

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 1,
    filterType: FilterType.ADVANCED,
    download: false,
    print: false,
    selectableRows: false,
    canDelete: false,
    canEdit: false,
    canInsert: !hideAddButton,
    customInsertEditModal: true,
    analytics: {
      customer: getCustomerDomain(),
      view: t('trades:dataTable.columns.analytics')
    },
    onRowInsert: async () => {
      history.push(NonAuthRoutes.tradesAdd, { currentTab: currentTab })
      return true
    },
    fabLabel: t('trades:dataTable.fab'),
    rowCustomActions: [
      {
        icon: <Pageview fontSize="small" />,
        tooltip: t('trades:dataTable.customActions.viewDetail'),
        onClick: onViewDetailClickHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Delete fontSize="small" />,
        tooltip: t('trades:dataTable.customActions.delete'),
        onClick: onDeleteClickHandler
      },
      {
        icon: <InsertDriveFile fontSize="small" />,
        tooltip: t('trades:dataTable.customActions.documents'),
        onClick: onDocumentAddHandler,
        userPermission: UserPermission.EDIT
      }
    ],
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '14px' }}>
        <DateFilter
          inactive={customFilter}
          enabled={!isFetching}
          initialFilter={
            dateFilter?.activeDateFilter === undefined
              ? DateFilters.ACTIVES
              : dateFilter.activeDateFilter
          }
          onSelectDate={onDateFilterChange}
          filters={[{ filter: DateFilters.ACTIVES }]}
        />
        <CustomFilter
          onApplyFilter={onApplyCustomFilter}
          onPrevalidateFilter={onPrevalidateCustomFilter}
          additionalFilters={searchFilterInputs}
          appliedFilter={customFilter}
          enabled={!isFetching}
        />
      </div>

      <DataTable
        data={trades}
        columns={columns}
        options={options}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={getUserPermissions(Pages.Trades)}
        appliedFilter={dataTableFilter}
        appliedSearch={dataTableSearch}
      />
      <ErrorDetailList {...errorDetailListDialogProps} />
    </>
  )
}
